import React from 'react';
import { Box, ButtonBase, Drawer, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import CustomSwitch from 'ui-components/CustomSwitch';
import { Close, Search } from '@mui/icons-material';

// ClickUp-style "Fields" panel: show/hide the list's columns where the list is. Same setting as
// Settings > Deal Flow > Table customization (hidden_columns), so both places stay in step.
// Name, the checkbox and Actions always show (the row needs them).
const LOCKED = ['checkbox', 'name', 'actions'];

const FieldRow = ({ label, checked, disabled, onToggle }) => (
    <Box
        component="label"
        sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
            minHeight: 34, px: '10px', borderRadius: '6px', cursor: disabled ? 'default' : 'pointer',
            color: disabled ? '#98A2B3' : '#101828', fontSize: '0.8125rem',
            '&:hover': { bgcolor: disabled ? 'transparent' : '#F9FAFB' },
        }}
    >
        <span>{label}</span>
        <CustomSwitch checked={checked} disabled={disabled} onChange={onToggle} inputProps={{ 'aria-label': `${checked ? 'Hide' : 'Show'} ${label}` }} />
    </Box>
);

const Section = ({ title, count, children }) => (
    <Box sx={{ mb: '12px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: '10px', py: '6px', fontSize: '0.75rem', fontWeight: 600, color: '#667085' }}>
            <span>{title}</span><span>{count}</span>
        </Box>
        {children}
    </Box>
);

const ColumnFieldsPanel = ({ open, onClose, columns = [], hiddenColumns = [], defaultHidden = [], onChange }) => {
    const [query, setQuery] = React.useState('');
    const hidden = React.useMemo(() => new Set(hiddenColumns), [hiddenColumns]);
    const keyOf = (col) => col.id || col.accessor;
    const matches = (col) => String(col.Header || '').toLowerCase().includes(query.trim().toLowerCase());
    // One list in column order: a toggle flips in place, the row never jumps to another section.
    const all = columns.filter((col) => keyOf(col) !== 'checkbox');
    const fields = all.filter(matches);
    const shownCount = all.filter((col) => !hidden.has(keyOf(col))).length;

    const toggle = (col) => {
        const key = keyOf(col);
        const next = new Set(hidden);
        next.has(key) ? next.delete(key) : next.add(key);
        onChange([...next]);
    };
    const isDefault = hiddenColumns.length === defaultHidden.length && defaultHidden.every((k) => hidden.has(k));

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            BackdropProps={{ sx: { bgcolor: 'transparent' } }}
            PaperProps={{ sx: { width: 320, maxWidth: '100vw', borderLeft: '1px solid #EAECF0', boxShadow: '-8px 0 24px rgba(16,24,40,0.08)' } }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '16px', pt: '16px', pb: '12px' }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#101828' }}>Fields</Typography>
                <IconButton size="small" onClick={onClose} aria-label="Close fields"><Close sx={{ fontSize: 18 }} /></IconButton>
            </Box>
            <Box sx={{ px: '16px', pb: '12px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: 36, px: '10px', border: '1px solid #D0D5DD', borderRadius: '8px', '&:focus-within': { borderColor: '#444CE7', boxShadow: '0 0 0 3px rgba(68,76,231,0.12)' } }}>
                    <Search sx={{ fontSize: 18, color: '#98A2B3' }} />
                    <InputBase value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search fields" inputProps={{ 'aria-label': 'Search fields' }} sx={{ flex: 1, fontSize: '0.875rem' }} />
                </Box>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', px: '6px' }}>
                <Section title="Columns" count={`${shownCount} of ${all.length} shown`}>
                    {fields.length === 0
                        ? <Typography sx={{ px: '10px', fontSize: '0.8125rem', color: '#98A2B3' }}>No field matches "{query.trim()}"</Typography>
                        : fields.map((col) => (
                            <FieldRow
                                key={keyOf(col)}
                                label={col.Header}
                                checked={!hidden.has(keyOf(col))}
                                disabled={LOCKED.includes(keyOf(col))}
                                onToggle={() => toggle(col)}
                            />
                        ))}
                </Section>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', px: '16px', py: '12px', borderTop: '1px solid #EAECF0' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#667085' }}>Hidden fields show in the row's expanded panel.</Typography>
                <Tooltip title={isDefault ? 'Already the default' : ''}>
                    <span>
                        <ButtonBase
                            disabled={isDefault}
                            onClick={() => onChange([...defaultHidden])}
                            sx={{ flexShrink: 0, px: '10px', height: 30, borderRadius: '6px', fontSize: '0.8125rem', fontWeight: 600, color: '#3538CD', '&.Mui-disabled': { color: '#98A2B3' }, '&:hover': { bgcolor: '#EEF0FF' } }}
                        >
                            Reset
                        </ButtonBase>
                    </span>
                </Tooltip>
            </Box>
        </Drawer>
    );
};

export default ColumnFieldsPanel;
